import React from 'react';
import { Badge, IconButton, Popover } from '@neshane/blueberry';
import { useNotifications } from '../hooks';
import './NotificationBell.css';

const CATEGORY_ICONS = {
    certificate: '📜',
    ekyc: '🪪',
    security: '🔐',
    payment: '💳',
    billing: '💳',
    legal: '⚖️',
    marketing: '📣',
    ticketing: '🎫',
    admin_agent: '🛎️',
};

function NotificationItem({ notification, onClick }) {
    const icon =
        CATEGORY_ICONS[notification.category] || CATEGORY_ICONS[notification.templateKey] || '🔔';

    return (
        <button
            type="button"
            className={`notification-bell-item${!notification.isRead ? ' unread' : ''}`}
            onClick={() => onClick?.(notification)}
        >
            <span className="notification-bell-item-icon">{icon}</span>
            <span className="notification-bell-item-body">
                <span className="notification-bell-item-title">{notification.title}</span>
                {notification.body && (
                    <span className="notification-bell-item-text">{notification.body}</span>
                )}
                {notification.channel && (
                    <span className={`notification-bell-item-channel ${notification.channel}`}>
                        {notification.channel}
                    </span>
                )}
            </span>
            {!notification.isRead && <span className="notification-bell-item-dot" />}
        </button>
    );
}

/**
 * NotificationBell - header bell with unread badge and dropdown.
 *
 * @param {string} scope - 'user' (inbox + unread) | 'admin' | 'operator' (sent list)
 */
export const NotificationBell = ({ scope = 'user' }) => {
    const {
        notifications,
        unreadCount,
        loading,
        error,
        refresh,
        markRead,
        markAllRead,
    } = useNotifications(scope);

    const isUserScope = scope === 'user';
    const badgeCount = isUserScope ? unreadCount : 0;
    const hasItems = notifications.length > 0;

    const handleItemClick = (notification) => {
        if (isUserScope && !notification.isRead) {
            markRead(notification.id);
        }
    };

    return (
        <Popover
            placement="bottom-end"
            className="notification-bell"
            renderTrigger={({ isDropdown: _, ...triggerProps }) => (
                <div className="notification-bell-trigger">
                    <IconButton
                        {...triggerProps}
                        iconName="bell"
                        variant="tertiaryNeutral"
                        aria-label="اعلان‌ها"
                    />
                    {badgeCount > 0 && (
                        <Badge className="notification-bell-badge">
                            {badgeCount > 99 ? '۹۹+' : badgeCount}
                        </Badge>
                    )}
                </div>
            )}
        >
            {(closeDropdown) => (
                <div className="notification-bell-panel" role="menu">
                    <div className="notification-bell-header">
                        <h3>اعلان‌ها</h3>
                        <div className="notification-bell-header-actions">
                            <button
                                type="button"
                                className="notification-bell-action"
                                onClick={refresh}
                                title="بروزرسانی"
                            >
                                ↻
                            </button>
                            {isUserScope && unreadCount > 0 && (
                                <button
                                    type="button"
                                    className="notification-bell-action"
                                    onClick={() => {
                                        markAllRead();
                                        closeDropdown();
                                    }}
                                >
                                    خواندن همه
                                </button>
                            )}
                        </div>
                    </div>

                    {loading && (
                        <div className="notification-bell-state">
                            <span className="notification-bell-spinner" />
                            <p>در حال بارگذاری...</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="notification-bell-state notification-bell-error">
                            <p>{error}</p>
                        </div>
                    )}

                    {!loading && !error && !hasItems && (
                        <div className="notification-bell-state">
                            <span className="notification-bell-empty-icon">🔔</span>
                            <p>اعلانی وجود ندارد</p>
                        </div>
                    )}

                    {!loading && !error && hasItems && (
                        <div className="notification-bell-list">
                            {notifications.map((notification) => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    onClick={handleItemClick}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </Popover>
    );
};

export default NotificationBell;